import { useEffect, useRef, useState } from "react";

import { Locales } from "@simplyk/common";
import { useSnackbar } from "notistack";
import { FieldValues } from "react-hook-form";

import { useTranslationActivityRegistration } from "../Context/TranslationActivityContext";
import { translateInBatches } from "../translateInBatches";

import { hasTranslationContent, TranslationRowRequest } from "./translationContent";
import { useTranslationWriteGuard } from "./useTranslationWriteGuard";

import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";

export const useTranslateRows = <T extends FieldValues>({
  rows,
  translationLocale,
  setValue,
}: {
  rows: TranslationRowRequest[];
  translationLocale: Locales;
  setValue: (path: string, value: string) => void;
}) => {
  const { mutateAsync: translateBatchText } = trpc.translateBatchText.useMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();
  const guard = useTranslationWriteGuard<T>();
  const startTranslation = useTranslationActivityRegistration();
  const initial = useRef({
    rows,
    translationLocale,
    setValue,
    guard,
    translateBatchText,
    enqueueSnackbar,
    errorMessage: t("dashboard", "formTranslation.alertMissingTranslations"),
  });
  const [isTranslating, setIsTranslating] = useState(rows.some((row) => row.fields.length > 0));
  const [translatedIds, setTranslatedIds] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    const session = initial.current;
    const pendingRows = session.rows
      .map((row) => ({ ...row, fields: row.fields.filter((field) => session.guard.prepare(field)) }))
      .filter((row) => row.fields.length > 0);
    const pendingIds = new Set(pendingRows.map((row) => row.id));
    setTranslatedIds(new Set(session.rows.filter((row) => !pendingIds.has(row.id)).map((row) => row.id)));
    setIsTranslating(pendingRows.length > 0);
    if (pendingRows.length === 0) {
      return;
    }
    const request = { active: true, incomplete: false };
    const stopActivity = startTranslation();
    const finish = (failed: boolean) => {
      if (!request.active) {
        return;
      }
      request.active = false;
      clearTimeout(timeout);
      stopActivity();
      setIsTranslating(false);
      if (failed) {
        session.enqueueSnackbar(session.errorMessage, {
          vibe: "danger",
          preventDuplicate: true,
        });
      }
    };
    const timeout = setTimeout(() => finish(true), 30_000);

    void translateInBatches<TranslationRowRequest, { id: string; [key: string]: unknown }>({
      items: pendingRows,
      translateBatch: async (batch) => {
        if (!request.active) {
          return [];
        }
        const response = await session.translateBatchText({
          texts: batch.map((row) => ({
            id: row.id,
            ...Object.fromEntries(row.fields.map((field) => [field.key, field.source])),
          })),
          targetLanguage: session.translationLocale,
        });
        return response.data?.translations ?? null;
      },
      onBatchTranslated: ({ translations, batch }) => {
        if (!request.active) {
          return;
        }
        const translationsById = new Map(translations.map((translation) => [translation.id, translation]));
        batch.forEach((row) => {
          row.fields.forEach((field) => {
            const translated = translationsById.get(row.id)?.[field.key];
            if (hasTranslationContent(translated, field.isPlainText)) {
              session.guard.write(field.path, translated, session.setValue);
            } else {
              request.incomplete = true;
            }
          });
        });
        setTranslatedIds((previous) => new Set([...previous, ...batch.map((row) => row.id)]));
      },
    }).then(({ hasFailedBatch }) => finish(hasFailedBatch || request.incomplete));

    return () => {
      request.active = false;
      clearTimeout(timeout);
      stopActivity();
    };
  }, [startTranslation]);

  return { isTranslating, translatedIds };
};
